import { useRef } from 'react';
import { useEntranceMotion } from '@/shared/hooks/use-entrance-motion';
import type { WorkspaceData } from '@/shared/types/workspace';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Pill } from '@/shared/ui/pill';
import { useFirstRun } from '../hooks/use-first-run';
import { AGENT_NAMES, stepMark } from '../model/first-run';
import { AgentChoice } from './agent-choice';
import { ClaudeKeyStep } from './claude-key-step';
import { CodexStep } from './codex-step';
import { FirstRunHero } from './first-run-hero';
import { PodFactsCard } from './pod-facts-card';
import { StepRow } from './step-row';
import { TestPodStep } from './test-pod-step';

type FirstRunViewProps = { workspace: WorkspaceData };

/** Connect an agent and run a test Pod (design: FirstRun). */
export function FirstRunView({ workspace }: FirstRunViewProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEntranceMotion(rootRef, 'first-run', {
    distance: 14,
    duration: 0.55,
    stagger: 0.08,
    floatPods: true,
  });
  const flow = useFirstRun(workspace);
  const { claudeCode, codex } = workspace.connections;
  const agentName = AGENT_NAMES[flow.agent];
  const isClaude = flow.agent === 'claude-code';

  return (
    <div ref={rootRef} className="contents">
      <FirstRunHero step={flow.step} isFree={workspace.plan === 'FREE'} />
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(520px,100%),1fr))] items-start gap-4">
        <Card asChild>
          <section data-enter="" aria-label="Connect" className="px-6 py-1">
            <StepRow number={1} mark={stepMark(1, flow.step, flow.isFinished)}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-semibold text-section">Where do your sessions start?</span>
                {flow.step > 1 ? (
                  <span className="text-muted">
                    {agentName}{' '}
                    <Button
                      variant="link"
                      size="inline"
                      className="ml-2 font-normal"
                      onClick={flow.toChoose}
                    >
                      Change
                    </Button>
                  </span>
                ) : null}
              </div>
              {flow.phase === 'choose' ? (
                <AgentChoice
                  agent={flow.agent}
                  onPick={flow.pickAgent}
                  onContinue={flow.toConnect}
                />
              ) : null}
            </StepRow>
            <StepRow number={2} mark={stepMark(2, flow.step, flow.isFinished)}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-semibold text-section">
                  {isClaude ? 'Paste the environment key' : 'Point the webhook at Macswarm'}
                </span>
                {flow.step > 2 ? (
                  <Pill tone="ok" hasDot>
                    Connected
                  </Pill>
                ) : null}
              </div>
              {flow.phase === 'connect' && isClaude ? (
                <ClaudeKeyStep
                  isRejected={claudeCode.status === 'KEY_REJECTED'}
                  onConnected={flow.toTest}
                />
              ) : null}
              {flow.phase === 'connect' && !isClaude ? (
                <CodexStep connection={codex} onContinue={flow.toTest} />
              ) : null}
            </StepRow>
            <StepRow number={3} mark={stepMark(3, flow.step, flow.isFinished)}>
              <span className="font-semibold text-section">Start a test Pod</span>
              {flow.phase === 'test' ? (
                <TestPodStep
                  run={workspace.latestTestPod}
                  agentName={agentName}
                  environmentName={claudeCode.environmentName}
                />
              ) : null}
            </StepRow>
          </section>
        </Card>
        <PodFactsCard />
      </div>
    </div>
  );
}
